import {
  formatName,
  matchesPredicate,
  ORDER_PREDICATES,
  type OrderUrgency,
  type OrderWithRelations,
  type PaymentStatus,
} from '@snootsvet/shared'

import type { BadgeVariant } from '@/frontend/components/ui/badge'

/** Urgency rank — higher is more urgent. */
export const urgencyRank: Record<OrderUrgency, number> = { high: 3, medium: 2, low: 1 }

/**
 * Returns true when an order still requires action — i.e. it would qualify for
 * the checkout queue or the remote dispensing queue. This is the set of orders
 * surfaced as "outstanding": awaiting preparation (`pending`), ready to hand
 * over (`prepared`), out of stock, or carrying an unpaid/failed balance
 * (`invoiceable`). Fulfilled, cancelled, and returned orders are excluded.
 */
export function isOutstandingOrder(order: {
  status: OrderWithRelations['status']
  paymentStatus: PaymentStatus
}): boolean {
  if (order.status === 'returned' || order.status === 'cancelled') return false
  return (
    order.status === 'pending' ||
    order.status === 'prepared' ||
    order.status === 'out_of_stock' ||
    matchesPredicate(order, ORDER_PREDICATES.invoiceable)
  )
}

// ---------------------------------------------------------------------------
// Order-level predicates
// ---------------------------------------------------------------------------

/**
 * Returns true when a fee can be manually zeroed — the order has an outstanding
 * unpaid (or failed) balance and is not already at 100% discount.
 */
export function isOrderFeeZeroable(order: { paymentStatus: PaymentStatus; discountBps: number }) {
  return (
    (order.paymentStatus === 'unpaid' || order.paymentStatus === 'failed') &&
    order.discountBps < 10000
  )
}

/**
 * Returns true when a zeroed fee can be restored — the order is at 100%
 * discount and the unit price is non-zero (i.e. the product itself isn't free).
 */
export function isOrderFeeUnzeroable(order: { discountBps: number; unitPrice: number }) {
  return order.discountBps === 10000 && order.unitPrice > 0
}

/**
 * Returns the payment badge label and colour for an order, or null if
 * the order is free (no badge needed).
 */
export function getPaymentDisplay(order: {
  paymentStatus: PaymentStatus
  totalPrice: number
}): { label: string; badgeVariant: BadgeVariant } | null {
  if (order.paymentStatus === 'free') return null
  if (order.paymentStatus === 'paid') return { label: 'Paid', badgeVariant: 'success' }
  if (order.paymentStatus === 'failed') return { label: 'Failed', badgeVariant: 'destructive' }
  return { label: 'Unpaid', badgeVariant: 'pending' }
}

// ---------------------------------------------------------------------------
// Patient grouping
// ---------------------------------------------------------------------------

/** A patient and all their orders — used by both the checkout queue and the dispensing queue. */
export interface PatientGroup {
  patientId: string
  patientName: string
  ownerId: string
  ownerName: string
  appointmentTime: string | null
  /** The appointment's clinic — fixes the zone `appointmentTime` is read in. */
  appointmentClinicId: string | null
  appointmentTypeId: string | null
  items: OrderWithRelations[]
}

/**
 * Groups a flat list of orders by patient. The backend handles filtering and
 * search — this function only reconstructs the patient grouping.
 */
export function groupOrders(orders: OrderWithRelations[]): PatientGroup[] {
  // Group by patient
  const map = new Map<string, PatientGroup>()
  for (const d of orders) {
    const existing = map.get(d.patientId)
    if (existing) {
      existing.items.push(d)
    } else {
      map.set(d.patientId, {
        patientId: d.patientId,
        patientName: d.patient.name,
        ownerId: d.patient.owner.id,
        ownerName: formatName(d.patient.owner),
        appointmentTime: d.consultation?.appointment?.startTime ?? null,
        appointmentClinicId: d.consultation?.appointment?.clinicId ?? null,
        appointmentTypeId: d.consultation?.appointment?.appointmentTypeId ?? null,
        items: [d],
      })
    }
  }

  return [...map.values()]
}
